import { AlertFactory, AlertState } from 'alerts/Alert'
import BillingMethod from 'billing/models/BillingMethod'
import { SelectPaymentMethodDelegate } from 'billing/payment-methods/PaymentMethodsRootViewModel'
import { CancellationToken } from 'infra/cancellation'
import Job, { PaymentInfoStatus } from 'jobs/Job'
import { ProvidePaymentInfoDelegate } from 'jobs/JobStore'
import { computed, makeObservable } from 'mobx'
import { BehaviorSubject, merge, Subject } from 'rxjs'
import {
  distinctUntilChanged,
  filter,
  skip,
  switchMap,
  takeUntil,
} from 'rxjs/operators'
import { toStream } from 'utils/mobx-util'
import {
  emitLatestWhen,
  shareLatest,
  whileActive,
  withCancellation,
} from 'utils/rx-util'

/**
 * Manages state around payment info requests.
 */
export class PaymentInfoCollectorViewModel {
  /**
   * Active tracker.
   *
   * @private
   */
  private readonly isActive$ = new BehaviorSubject(false)

  /**
   * Used to manually show the payment info collector.
   *
   * @private
   */
  private readonly showPaymentInfoCollector$ = new Subject()

  /**
   * Emits when payment info is requested.
   * @private
   */
  private readonly paymentInfoRequested$ = toStream(
    () => this.canProvidePaymentInfo,
    true,
  ).pipe(
    distinctUntilChanged(),
    emitLatestWhen(this.showPaymentInfoCollector$),
    filter(Boolean),
    shareLatest(),
    whileActive(this.isActive$),
  )

  /**
   * Flow that runs while the VM is active.
   *
   * @private
   */
  private providePaymentInfoFlow$ = this.paymentInfoRequested$.pipe(
    switchMap(() => withCancellation((ct) => this.providePaymentInfo(ct))),
    whileActive(this.isActive$),
  )

  constructor(
    private job: Job,
    private selectPaymentMethodDelegate: SelectPaymentMethodDelegate,
    private providePaymentInfoDelegate: ProvidePaymentInfoDelegate,
    private newAlert: AlertFactory,
    private revealEventsDelegate: () => void,
  ) {
    makeObservable(this)
    this.show = this.show.bind(this)
    this.providePaymentInfoFlow$.subscribe()
  }

  /**
   * Whether we can provide payment info.
   */
  @computed
  get canProvidePaymentInfo() {
    const isClientChamp =
      this.job.currentUserCollaborator?.is('CLIENT', 'CHAMPION') ?? false
    return (
      isClientChamp &&
      this.job.paymentInfoStatus === PaymentInfoStatus.Requested
    )
  }

  /**
   * Activates the VM.
   */
  activate() {
    this.isActive$.next(true)
  }

  /**
   * Deactivates the VM.
   */
  deactivate() {
    this.isActive$.next(false)
  }

  /**
   * Shows the collector if applicable.
   */
  show() {
    this.showPaymentInfoCollector$.next(undefined)
  }

  /**
   * Provides payment info.
   *
   * @param ct
   */
  async providePaymentInfo(ct: CancellationToken) {
    if (!this.job.currentUserCollaborator) {
      // Should never happen since it gets filtered out by the caller.
      return null
    }

    const method = await this.selectPaymentMethodDelegate(
      this.job.currentUserCollaborator?.member,
      this.job.currentUserCollaborator?.team,
      null,
      (method) => this.savePaymentMethod(method),
      ct,
    )
    if (ct.isCancelled) {
      return
    }

    if (!method) {
      // The user cancelled. Show an alert with a quick way to get back here.
      const alert = this.newAlert()
        .saying(
          "We're ready when you are!",
          'Click here to provide payment info to continue. You can also find this under Actions.',
        )
        .onInteract(this.show)
        .show()

      // When we deactivate or the collector is shown again, dismiss the alert.
      merge(
        this.paymentInfoRequested$.pipe(skip(1)),
        this.isActive$.pipe(filter((v) => !v)),
      )
        .pipe(takeUntil(alert.dismissed$))
        .subscribe(alert.dismiss)
    }
  }

  /**
   * Saves the payment method.
   *
   * @param method
   */
  async savePaymentMethod(method: BillingMethod) {
    const alert = this.newAlert()
      .withState(AlertState.Pending)
      .saying('Submitting payment information', 'Hang tight!')
      .show()
    try {
      await this.providePaymentInfoDelegate(this.job, method)
      await new Promise((resolve) => setTimeout(resolve, 2000))
      alert
        .withState(AlertState.Success)
        .saying(
          'Payment info has been recorded!',
          'A charge will be attempted shortly.',
        )
        .withActions(
          {
            title: 'View Events',
            handler: this.revealEventsDelegate,
          },
          { title: 'Close', handler: alert.dismiss },
        )
    } catch (err) {
      alert.fromError(err)
    }
  }
}
